import { useCallback, useRef, useState } from 'react'
import { API_ORIGIN } from '@/constants'
import { accessRequestEnabled, getAccessRequestConfig } from '@/constants/signup'
import { captchaRequiredForAction } from '@/lib/captcha'
import { useCaptchaConfig } from '@/hooks/use-captcha-config'
import type { CaptchaFieldHandle } from '@/components/auth/captcha-field'

const ACCESS_REQUEST_URL = API_ORIGIN
  ? `${API_ORIGIN}/api/accounts/access-request`
  : '/api/accounts/access-request'

export type AccessRequestFormValues = {
  email: string
  name: string
  role: string
  company: string
  note: string
}

function fieldValueForId(id: string, values: AccessRequestFormValues): string {
  if (id === 'name') return values.name
  if (id === 'role') return values.role
  if (id === 'company') return values.company
  if (id === 'note') return values.note
  return ''
}

export function useAccessRequestSubmit() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [company, setCompany] = useState('')
  const [note, setNote] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<CaptchaFieldHandle>(null)
  const pendingCaptchaSubmitRef = useRef(false)
  const { config: captchaConfig } = useCaptchaConfig()
  const live = accessRequestEnabled()
  const captchaRequired = live && captchaRequiredForAction(captchaConfig, 'waitlist')
  const captchaInvisible = captchaConfig?.widget?.size === 'invisible'
  const accessRequestConfig = getAccessRequestConfig()

  const submitAccessRequest = useCallback(
    async (token?: string | null) => {
      if (!live) {
        setSubmitted(true)
        return
      }
      setSubmitting(true)
      setError('')
      try {
        const response = await fetch(ACCESS_REQUEST_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            name: name || undefined,
            role: role || undefined,
            company: company || undefined,
            note: note || undefined,
            captcha_token: token ?? undefined,
          }),
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          setError(data.error || 'Something went wrong. Please try again.')
          captchaRef.current?.reset()
          setCaptchaToken(null)
          return
        }
        setSubmitted(true)
      } catch {
        setError('Could not reach the server. Please try again in a moment.')
      } finally {
        setSubmitting(false)
      }
    },
    [live, email, name, role, company, note],
  )

  const handleCaptchaTokenChange = useCallback(
    (token: string | null) => {
      setCaptchaToken(token)
      if (token && pendingCaptchaSubmitRef.current) {
        pendingCaptchaSubmitRef.current = false
        void submitAccessRequest(token)
      }
    },
    [submitAccessRequest],
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setError('')

      if (live && !email.trim()) {
        setError('Please enter your email address.')
        return
      }

      const values: AccessRequestFormValues = { email, name, role, company, note }
      for (const field of accessRequestConfig.fields ?? []) {
        if (!field.required) continue
        if (!fieldValueForId(field.id, values).trim()) {
          const label = field.label ?? field.placeholder ?? field.id
          setError(`Please enter ${label.toLowerCase()}.`)
          return
        }
      }

      if (captchaRequired && !captchaToken) {
        if (captchaInvisible) {
          pendingCaptchaSubmitRef.current = true
          const started = captchaRef.current?.execute()
          if (started === false) {
            pendingCaptchaSubmitRef.current = false
            setError('Security check is still loading. Please wait a moment and try again.')
          }
          return
        }
        setError('Please complete the captcha verification.')
        return
      }

      void submitAccessRequest(captchaToken)
    },
    [
      live,
      email,
      name,
      role,
      company,
      note,
      accessRequestConfig.fields,
      captchaRequired,
      captchaToken,
      captchaInvisible,
      submitAccessRequest,
    ],
  )

  return {
    accessRequestConfig,
    submitted,
    submitting,
    error,
    email,
    setEmail,
    name,
    setName,
    role,
    setRole,
    company,
    setCompany,
    note,
    setNote,
    captchaRef,
    captchaRequired,
    live,
    handleCaptchaTokenChange,
    handleSubmit,
    handleCaptchaMisconfigured: () => {
      setError('Sign-up is temporarily unavailable. Please contact support.')
    },
    handleCaptchaExecuteError: () => {
      pendingCaptchaSubmitRef.current = false
      setError('Captcha verification failed. Please try again.')
    },
  }
}
